/**
 * Share formatting for the choice plots (plots plan J).
 *
 * Python rounds every share once, half away from zero, and stamps the decimals it
 * rounded to (`data.decimals`). The plot prints the stamped value with at most
 * those decimals and drops a trailing zero ("39%", "38.8%"), grouped by `Intl`
 * in the request locale. It never rounds again: a stamped `38.8` prints
 * `38.8`, whatever the locale.
 *
 * Stored payloads that carry no `decimals` go through their read adapter's
 * legacy formatter instead (`legacyChoiceShare`), today's slide rule.
 */

/** What an unlabelled payload prints in. */
export const CHOICE_FALLBACK_LOCALE = 'en'

function safeLocale(locale: string | null | undefined): string {
  return typeof locale === 'string' && locale.trim() ? locale.trim() : CHOICE_FALLBACK_LOCALE
}

/**
 * A stamped share (0-100) as a percent label, at most `decimals` digits.
 * `fixed` keeps the trailing zero ("52.0%"): the survey tab's one-decimal
 * plots, whose digits are a rule rather than the producer's rounding.
 */
export function formatChoiceShare(share: number, decimals: number, locale?: string | null, fixed = false): string {
  const digitCount = Math.max(0, decimals)
  const options: Intl.NumberFormatOptions = { minimumFractionDigits: fixed ? digitCount : 0, maximumFractionDigits: digitCount }
  let digits: string
  try {
    digits = new Intl.NumberFormat(safeLocale(locale), options).format(share)
  } catch {
    digits = new Intl.NumberFormat(CHOICE_FALLBACK_LOCALE, options).format(share)
  }
  return `${digits}%`
}

/**
 * Legacy only: today's slide rule for a stored value that carries no
 * decimals (`SingleSelectBarsSlide` `fmtPercent`): whole numbers print whole,
 * anything else to one decimal.
 */
export function legacyChoiceShare(value: number): string {
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`
}

/**
 * Currency signs short enough to sit against the figure (today's
 * `DistributionBars` list, spelled out rather than `\p{Sc}`).
 */
const LEGACY_CURRENCY_SIGNS = '$\u20ac\u00a3\u00a5\u20b9\u20bd\u20a9\u20aa\u20ab\u20b4\u20a6\u20b1\u0e3f'
const LEGACY_SUFFIX_SYMBOL = new RegExp(`^(%|\\/\\s?\\d+(\\.\\d+)?|[${LEGACY_CURRENCY_SIGNS}])$`)

/**
 * Legacy: a stored distribution card's figure rule, moved from
 * `DistributionBars` (one decimal, whole numbers whole). The stored `unit` is
 * glued on when it is a short suffix symbol ("%", "/5", a currency sign), a
 * prose unit prints the bare figure, and no unit prints a percent.
 */
export function legacyCardShareFor(unit: string | null | undefined): (value: number) => string {
  return (value: number) => {
    const rounded = Math.round(value * 10) / 10
    if (unit === null || unit === undefined) {
      return `${rounded}%`
    }
    return LEGACY_SUFFIX_SYMBOL.test(unit.trim()) ? `${rounded}${unit}` : String(rounded)
  }
}

/** Fills `{key}` placeholders in a Python chrome template; null when there is no template. */
export function fillChoiceTemplate(template: string | undefined, values: Record<string, string | number>): string | null {
  if (!template) {
    return null
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in values ? String(values[key]) : match))
}

/** Print the selected unit from stamped figures, shared by bars and donut. */
export function choiceFigure(
  entry: { share: number; count?: number | null },
  shareUnit: 'percent' | 'count',
  formatShare: (share: number) => string,
  locale?: string | null,
): string {
  if (shareUnit === 'count') {
    if (typeof entry.count !== 'number' || !Number.isFinite(entry.count)) return '—'
    return new Intl.NumberFormat(locale ?? undefined, { maximumFractionDigits: 0 }).format(entry.count)
  }
  return formatShare(entry.share)
}
